import { Link, usePage } from "@inertiajs/react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { SoundToggle } from "@/components/ui/sound-toggle"
import { site } from "./content"
import logoUrl from "@/assets/mt-logo.svg"
import type { PageProps } from "@/types/inertia"

// Minimal sticky bar: wordmark left, Apply CTA right. Pure CSS sticky, no JS.
//
// It works out its own sticky offset rather than being told, because getting it
// wrong is invisible to everyone except a signed-in admin — the hardest kind of
// layout bug to notice. For an admin, PublicShell puts the nav rail on the page:
// from lg that rail is a fixed column beside this, so the header still starts at
// the top, but below lg it collapses to a drawer with its own h-12 opener bar,
// and this has to stack under it. For a visitor there is no rail and no offset.
//
// `applyHref` defaults to the on-page #apply anchor, which is right for every
// page that embeds the lead form. Pages that don't carry the form (the dead-link
// page) pass "/#apply" so the CTA goes home instead of being a dead anchor.
export function SiteHeader({ applyHref = "#apply" }: { applyHref?: string }) {
  const isAdmin = usePage<PageProps>().props.current_user?.admin ?? false
  // The logo does the conventional thing for wherever it's rendered: on the
  // home page it scrolls back to the top, and on any other page (the case
  // studies) it navigates home. Derived from the current URL rather than passed
  // in, so a new page gets the right behaviour without threading a prop.
  const { url } = usePage()
  const isHome = url === "/" || url.startsWith("/?") || url.startsWith("/#")

  // alt="" on the image throughout — the link's aria-label already announces
  // the name, so the img is decorative and would otherwise be read twice.
  const logo = <img src={logoUrl} alt="" className="h-10 w-auto" />

  return (
    <header
      className={cn(
        "sticky z-40 border-b border-hairline bg-page/80 backdrop-blur",
        isAdmin ? "top-12 lg:top-0" : "top-0",
      )}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        {isHome ? (
          <a href="#top" aria-label={`${site.name}, back to top`}>
            {logo}
          </a>
        ) : (
          <Link href="/" aria-label={`${site.name}, back to the home page`}>
            {logo}
          </Link>
        )}
        <div className="flex items-center gap-1">
          <SoundToggle />
          <Button asChild size="sm" className="min-w-28">
            <a href={applyHref} data-track-click="header_apply" data-sound="cta_click">
              Apply
            </a>
          </Button>
        </div>
      </div>
    </header>
  )
}
